/**
 * Product voice, in one place.
 *
 * RackSavant is written like magazine copy, not app copy. Short declaratives.
 * No exclamation marks. No emoji as punctuation. Nothing "unlocks", nothing is
 * "seamless", nobody is "empowered", and we never say "slay". Lowercase where a
 * caption would be lowercase; capitalised where a headline would be set.
 *
 * The register: a stylist who is fond of you and has somewhere to be.
 *
 * The one rule that outranks style — we never compliment, rate, or reference a
 * body. We compliment choices. Loading states get the most attention here,
 * because waiting is where anticipation is manufactured.
 */

import { RACKSAVANT, ROOM_CAPACITY } from '@racksavant/core';

export const VOICE = {
  tagline: 'Try on the feeling.',
  subTagline: 'Your photo. Any garment. The mood you were reaching for.',
  /** Masthead strapline, set small and tracked out under the wordmark. */
  strapline: 'A fitting room that never closes',
  /** Runs in the marquee. Fragments, not sentences. */
  marquee: [
    'try on the feeling',
    'no measurements',
    'nothing is edited but the clothes',
    'the group chat votes',
    'delete means delete',
  ],
} as const;

/** Rotating lines shown while a look develops. Order is deliberate. */
export const DEVELOPING_LINES = [
  'Reading the room',
  'Pulling it off the rack',
  'Draping',
  'Finding the shoulder line',
  'Setting the lights',
  'Letting it fall',
  'Developing',
  'Reading the mood',
] as const;

export const EMPTY_STATES = {
  noLooks: {
    title: 'Nothing on the rack',
    body: 'One full-body photo. Everything after that happens here.',
    action: 'Start with a photo',
  },
  noBodyShots: {
    title: 'One photo of you',
    body: 'Full body, decent light, phone at chest height. That is the whole ask.',
    action: 'Add a photo',
  },
  noLookbooks: {
    title: 'No eras',
    body: 'This is how a scattered camera roll turns into a story.',
    action: 'Name your first era',
  },
  noFollowing: {
    title: 'Quiet in here',
    body: 'Follow people. Their looks land here first.',
    action: 'Find people',
  },
  noNotifications: {
    title: 'All caught up',
    body: 'Hype, invites and finished fittings show up here.',
    action: '',
  },
  roomEmpty: {
    title: 'Just you so far',
    body: 'Send the code. Fitting rooms are better loud.',
    action: 'Copy the code',
  },
} as const;

export const PHOTO_TIPS = [
  'Head to shoes in frame',
  'Phone at chest height, straight on',
  'A plain wall beats a busy room',
  'Fitted clothes read better than bulky layers',
  'Daylight in front of you, not behind',
] as const;

export const ONBOARDING = {
  step1: {
    eyebrow: 'One',
    title: 'One photo of you',
    body: 'It stays private unless you post it. You can delete it any time, and it goes for real.',
  },
  step2: {
    eyebrow: 'Two',
    title: 'Pick a mood, not a size',
    body: 'Soft. Sharp. Feral. Choose how you want to feel and we pull the rack to match.',
  },
  step3: {
    eyebrow: 'Three',
    title: 'Open the room',
    body: 'Invite the group chat. They react live while you try things on. That is the whole app.',
  },
} as const;

/** Shown once, the first time a user opens the app after signing up. */
export const WELCOME_LINES = [
  'Getting dressed is the first decision you make about who you are today.',
  'This is a mirror that remembers.',
] as const;

/**
 * Shown after someone asks for a reset link.
 *
 * There is no transactional email provider wired up yet, so the honest thing is
 * to say so rather than let a user sit waiting for mail that will never arrive.
 * Replace this the moment RESEND_API_KEY is set — see docs/AUTH_SETUP.md.
 */
export const PASSWORD_RESET_NOTICE = {
  title: "we didn't implement reset email yet, lol",
  body: 'email me at jacobfv123@gmail.com and i can email u a reset code',
  email: 'jacobfv123@gmail.com',
} as const;

export const PRIVACY_PROMISES = [
  'Your photos are private by default. Nothing is public until you make it public.',
  'We never train models on your photos.',
  'Delete a photo and every look made from it goes with it.',
  'No body measurements, ever. We do not ask, store, or infer them.',
] as const;

export const SHARE_SHEET = {
  title: 'Send it out',
  options: [
    { id: 'story', label: 'Story-ready (9:16)', hint: 'Vertical with your mood read burned in' },
    { id: 'post', label: 'Feed post (4:5)', hint: 'Clean crop, no watermark on paid plans' },
    { id: 'sidebyside', label: 'Before / after', hint: 'The original and the look, split' },
    { id: 'link', label: 'Copy link', hint: 'Anyone with the link can view' },
  ],
} as const;

/** Nudges that appear on the result screen — the moment of highest feeling. */
export const RESULT_PROMPTS = [
  'Save it to an era',
  'Ask the room',
  'Get a caption',
  'Try it in another mood',
] as const;

/**
 * Plan copy. Every allowance quoted here is interpolated from the shared
 * contract in `@racksavant/core`, never typed out — the pricing page and the
 * API had already drifted apart once on room capacity.
 */
export const PLANS = {
  free: {
    name: 'Free',
    price: 'Free',
    line: 'For seeing what this is.',
    features: [
      `${RACKSAVANT.freeFittingsPerDay} fittings a day`,
      'Full garment gallery',
      'Mood reads on every look',
      `Fitting rooms with up to ${ROOM_CAPACITY.free} friends`,
    ],
  },
  muse: {
    name: 'Muse',
    price: '$12',
    period: '/mo',
    line: 'For people who post.',
    features: [
      'Unlimited fittings',
      'No watermark',
      'Caption studio, all six tones',
      `Rooms up to ${ROOM_CAPACITY.muse}`,
      'Story + feed exports',
      'Private lookbooks',
    ],
  },
  studio: {
    name: 'Studio',
    price: '$39',
    period: '/mo',
    line: 'For creators with a brand behind them.',
    features: [
      'Everything in Muse',
      'Upload your own garments',
      'Publish drops to your followers',
      'Analytics on every look',
      'Priority rendering',
      'Commercial licence',
    ],
  },
} as const;
